import { useCallback, useEffect } from "react";

/**
 * Runs `callback` on Escape. `exclusive` claims the key: the listener runs in
 * the capture phase and stops propagation, so a page-level Escape (a "go
 * back") does not also fire while an overlay is open.
 */
export const useEscape = (
  callback: () => void,
  enabled = true,
  exclusive = false,
) => {
  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (exclusive) event.stopPropagation();
        callback();
      }
    },
    [callback, exclusive],
  );

  useEffect(() => {
    if (!enabled) return;
    document.addEventListener("keydown", handleKeyPress, exclusive);
    return () => {
      document.removeEventListener("keydown", handleKeyPress, exclusive);
    };
  }, [handleKeyPress, enabled, exclusive]);
};

export const useEnter = (callback: () => void) => {
  const handleKeyPress = useCallback(
    (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        callback();
      }
    },
    [callback],
  );
  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);
};
